import React, { useState, useEffect, useRef, memo } from 'react';
import { Box, SxProps } from '@mui/material';

interface ImageProps {
  src: string;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  sources: {
    srcSet: string;
    media: string;
  }[];
  placeholder?: string;
  errorPlaceholder?: string;
  sx?: SxProps;
}

// 使用 memo 包装组件以避免不必要的重新渲染
export const LazyLoadedResponsiveImage: React.FC<ImageProps> = memo(
  ({
    src,
    alt = '',
    className,
    width = '100%',
    height = 'auto',
    sources,
    placeholder,
    errorPlaceholder,
    sx,
  }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const imgRef = useRef<HTMLPictureElement | null>(null);

    // 当图片加载完成时，更新状态以显示图片
    useEffect(() => {
      if (isVisible && imgRef.current) {
        const img = imgRef.current.querySelector('img');

        const handleLoad = () => {
          setLoaded(true);
        };

        const handleError = () => {
          setError(true);
        };

        if (img) {
          img.addEventListener('load', handleLoad);
          img.addEventListener('error', handleError);
        }

        return () => {
          if (img) {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
          }
        };
      }
    }, [isVisible]);

    // 使用 IntersectionObserver 检测图片是否出现在视口中
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
            }
          });
        },
        {
          threshold: 0.1,
        },
      );

      if (imgRef.current) {
        observer.observe(imgRef.current);
      }

      return () => {
        if (imgRef.current) {
          observer.unobserve(imgRef.current);
        }
      };
    }, []);

    return (
      <Box
        component="picture"
        className={className}
        ref={imgRef}
        sx={{
          '& img': {
            transition: 'opacity 0.5s',
            opacity: loaded ? 1 : 0,
          },
          ...sx,
        }}
      >
        {/* 为每个 media 查询生成 source 元素 */}
        {sources.map((source, index) => (
          <source
            key={index}
            srcSet={isVisible ? source.srcSet : ''}
            media={source.media}
          />
        ))}
        {/* 如果图片加载出错，则显示 errorPlaceholder，否则显示占位符或原始图片 */}
        <img
          src={
            error
              ? errorPlaceholder || src
              : isVisible
                ? src
                : placeholder || ''
          }
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
        />
      </Box>
    );
  },
);

LazyLoadedResponsiveImage.displayName = 'LazyLoadedResponsiveImage';

export default LazyLoadedResponsiveImage;

// Example：
// const Example = () => (
//   <LazyLoadedResponsiveImage
//     src="https://images.unsplash.com/photo-1507149834191-76de3246a685"
//     alt="example image
//     width={600}
//     height={400}
//     sources={[
//       {
//         srcSet:
//           'https://images.unsplash.com/photo-1507149834191-76de3246a685?w=320',
//         media: '(max-width: 320px)',
//       },
//       {
//         srcSet:
//           'https://images.unsplash.com/photo-1507149834191-76de3246a685?w=480',
//         media: '(max-width: 480px)',
//       },
//       {
//         srcSet:
//           'https://images.unsplash.com/photo-1507149834191-76de3246a685?w=800',
//         media: '(min-width: 800px)',
//       },
//     ]}
//     placeholder="https://images.unsplash.com/photo-1507149834191-76de3246a685?w=20&blur=5"
//     errorPlaceholder="https://images.unsplash.com/photo-1507149834191-76de3246a685?w=20&blur=5"
//   />
// );
//
// export default Example;
